import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Landmark, Banknote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankAccountSelector({ 
  value, 
  onChange, 
  paymentMethod = 'bank',
  disabled = false,
  showBalance = true,
  className = ''
}) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/accounting/bank-accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAccounts(data);
          
          // Auto-select default or first bank account if not cash and no value selected
          if (paymentMethod !== 'cash' && !value) {
            const defaultBank = data.find(a => a.is_default && a.type === 'bank');
            const firstBank = data.find(a => a.type === 'bank');
            if (defaultBank) {
              onChange(defaultBank.id);
            } else if (firstBank) {
              onChange(firstBank.id);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch bank accounts:', err);
      }
      setLoading(false);
    };
    
    fetchAccounts();
  }, [token, paymentMethod]);

  // Filter accounts based on payment method
  const filteredAccounts = paymentMethod === 'cash' 
    ? accounts.filter(a => a.type === 'cash')
    : accounts.filter(a => a.type === 'bank');

  // For cash payment, auto-select cash account
  useEffect(() => {
    if (paymentMethod === 'cash') {
      const cashAccount = accounts.find(a => a.type === 'cash');
      if (cashAccount && value !== cashAccount.id) {
        onChange(cashAccount.id);
      }
    }
  }, [paymentMethod, accounts]);

  if (loading) {
    return <div className="text-sm text-gray-500">Loading accounts...</div>;
  }

  // For cash payment, just show info (no selection needed)
  if (paymentMethod === 'cash') {
    const cashAccount = accounts.find(a => a.type === 'cash');
    if (cashAccount) {
      return (
        <div className={`flex items-center gap-2 p-2 bg-green-50 rounded border ${className}`}>
          <Banknote className="w-4 h-4 text-green-600" />
          <span className="text-sm">Cash in Hand</span>
          {showBalance && (
            <span className="text-xs text-gray-500 ml-auto">
              Balance: ₹{cashAccount.current_balance?.toLocaleString()}
            </span>
          )}
        </div>
      );
    }
  }

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder="Select Bank Account" />
      </SelectTrigger>
      <SelectContent>
        {filteredAccounts.map(account => (
          <SelectItem key={account.id} value={account.id}>
            <div className="flex items-center gap-2">
              <Landmark className="w-4 h-4 text-blue-600" />
              <span>{account.bank_name || account.account_name}</span>
              {account.account_number && (
                <span className="text-xs text-gray-500">
                  ****{account.account_number.slice(-4)}
                </span>
              )}
              {account.is_default && (
                <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">Default</span>
              )}
              {showBalance && (
                <span className="text-xs text-gray-500 ml-2">
                  ₹{account.current_balance?.toLocaleString()}
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
